import { useQuery } from '@tanstack/react-query';
import { seekSupabase } from '@/integrations/supabase/seekClient';
import { useSeekAuth } from '@/contexts/SeekAuthContext';

export interface SeekOfferSession {
  offer_id: string;
  token: string;
  status: string;
  offer_title: string | null;
  salary_amount: number | null;
  salary_currency: string | null;
  work_location: string | null;
  employment_type: string | null;
  expires_at: string | null;
  sent_at: string | null;
  responded_at: string | null;
  created_at: string;
  // Job info from seek_job_catalog
  job_title: string;
  org_name: string | null;
  seek_app_id: string;
}

export function useSeekOfferSessions() {
  const { user } = useSeekAuth();
  const uid = user?.id;

  const query = useQuery({
    queryKey: ['seek-offer-sessions', uid],
    queryFn: async () => {
      // Get candidate's seek_applications with job info
      const { data: apps, error: appErr } = await seekSupabase
        .from('seek_applications')
        .select('id, hire_application_id, seek_job_catalog(title, org_name)')
        .eq('candidate_id', uid!)
        .not('hire_application_id', 'is', null);
      if (appErr) throw appErr;
      if (!apps || apps.length === 0) return [];

      const hireAppIds = apps.flatMap((app) => app.hire_application_id ? [app.hire_application_id] : []);
      if (hireAppIds.length === 0) return [];

      // Get offers for those applications
      const { data: offers, error: offErr } = await seekSupabase
        .from('offers')
        .select('id, application_id, status, offer_title, salary_amount, salary_currency, work_location, employment_type, expires_at, sent_at, responded_at, created_at, token')
        .in('application_id', hireAppIds)
        .order('created_at', { ascending: false });
      if (offErr) throw offErr;
      if (!offers || offers.length === 0) return [];

      // Build lookup
      const appMap: Record<string, { seek_app_id: string; job_title: string; org_name: string | null }> = {};
      for (const a of apps) {
        if (a.hire_application_id) {
          appMap[a.hire_application_id] = {
            seek_app_id: a.id,
            job_title: a.seek_job_catalog?.title ?? 'Unknown Job',
            org_name: a.seek_job_catalog?.org_name ?? null,
          };
        }
      }

      return offers.map((o): SeekOfferSession => {
        const appInfo = appMap[o.application_id];
        return {
          offer_id: o.id,
          token: o.token,
          status: o.status,
          offer_title: o.offer_title,
          salary_amount: o.salary_amount,
          salary_currency: o.salary_currency,
          work_location: o.work_location,
          employment_type: o.employment_type,
          expires_at: o.expires_at,
          sent_at: o.sent_at,
          responded_at: o.responded_at,
          created_at: o.created_at,
          job_title: appInfo?.job_title ?? 'Unknown Job',
          org_name: appInfo?.org_name ?? null,
          seek_app_id: appInfo?.seek_app_id ?? '',
        };
      });
    },
    enabled: !!uid,
  });

  const offers = query.data ?? [];
  const now = new Date();
  const activeCount = offers.filter(o =>
    (o.status === 'sent' || o.status === 'viewed') && (!o.expires_at || new Date(o.expires_at) > now)
  ).length;

  return {
    offers,
    isLoading: query.isLoading,
    activeCount,
  };
}
